import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useRole } from "@/lib/roles";
import { Plus, Pencil, Trash2 } from "lucide-react";

export default function Clients() {
  const { t } = useTranslation("clients");
  const { t: tc } = useTranslation("common");
  const { canEdit } = useRole();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const refresh = () => {
    setLoading(true);
    api.get("/clients").then((r) => setClients(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  const startNew = () => { setEditing({ name: "", company: "", contact_name: "", email: "", phone: "", nif: "", notes: "" }); setOpen(true); };
  const startEdit = (c) => { setEditing({ ...c }); setOpen(true); };

  const handleDelete = async (id) => {
    if (!window.confirm(t("deleteConfirm"))) return;
    await api.delete(`/clients/${id}`);
    refresh();
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = { ...editing };
    if (payload.id) await api.put(`/clients/${payload.id}`, payload);
    else await api.post("/clients", payload);
    setOpen(false);
    setEditing(null);
    refresh();
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-zinc-950">{t("title")}</h1>
          <p className="text-sm text-zinc-500 mt-1">{t("subtitle", { count: clients.length })}</p>
        </div>
        <button onClick={startNew} data-testid="new-client-button" className={`inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm font-semibold ${canEdit ? "" : "hidden"}`}>
          <Plus className="w-4 h-4" /> {t("newClient")}
        </button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        {loading ? <div className="p-10 text-center text-sm text-zinc-500">{tc("states.loading")}</div> : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50/50 text-xs font-semibold text-zinc-500 uppercase tracking-wider border-b border-zinc-200">
              <tr>
                <th className="text-left py-3 px-4">{t("table.name")}</th>
                <th className="text-left py-3 px-4 hidden sm:table-cell">{t("table.company")}</th>
                <th className="text-left py-3 px-4 hidden md:table-cell">{t("table.contact")}</th>
                <th className="text-left py-3 px-4 hidden lg:table-cell">{t("table.email")}</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                  <td className="py-3 px-4 font-medium text-zinc-900">{c.name}</td>
                  <td className="py-3 px-4 text-zinc-700 hidden sm:table-cell">{c.company || "—"}</td>
                  <td className="py-3 px-4 text-zinc-700 hidden md:table-cell">{c.contact_name || "—"}</td>
                  <td className="py-3 px-4 text-zinc-700 hidden lg:table-cell">{c.email || "—"}</td>
                  <td className="py-3 px-4 text-right">
                    {canEdit && (
                      <div className="flex gap-2 justify-end">
                        <button data-testid={`edit-client-${c.id}`} onClick={() => startEdit(c)} className="text-xs text-zinc-700 hover:text-zinc-950 inline-flex items-center gap-1"><Pencil className="w-3 h-3" /> {tc("actions.edit")}</button>
                        <button data-testid={`delete-client-${c.id}`} onClick={() => handleDelete(c.id)} className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> {tc("actions.delete")}</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {clients.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-sm text-zinc-500">{t("empty")}</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      {open && editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <form onSubmit={handleSave} className="bg-white rounded-2xl w-full max-w-lg p-6 space-y-4" data-testid="client-modal">
            <h2 className="font-display text-xl font-bold text-zinc-950">{editing.id ? t("form.editTitle") : t("form.newTitle")}</h2>
            <div className="space-y-3">
              <Input label={t("form.name")} required value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="client-name" />
              <Input label={t("form.company")} value={editing.company} onChange={(v) => setEditing({ ...editing, company: v })} testid="client-company" />
              <Input label={t("form.contact")} value={editing.contact_name} onChange={(v) => setEditing({ ...editing, contact_name: v })} testid="client-contact" />
              <div className="grid grid-cols-2 gap-3">
                <Input label={t("form.email")} value={editing.email} onChange={(v) => setEditing({ ...editing, email: v })} testid="client-email" />
                <Input label={t("form.phone")} value={editing.phone} onChange={(v) => setEditing({ ...editing, phone: v })} testid="client-phone" />
              </div>
              <Input label={t("form.nif")} value={editing.nif} onChange={(v) => setEditing({ ...editing, nif: v })} testid="client-nif" />
              <Input label={t("form.notes")} value={editing.notes} onChange={(v) => setEditing({ ...editing, notes: v })} testid="client-notes" textarea />
            </div>
            <div className="flex gap-3 pt-2 border-t border-zinc-100">
              <button type="submit" data-testid="save-client" className="bg-zinc-950 hover:bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm font-semibold">{tc("actions.save")}</button>
              <button type="button" onClick={() => { setOpen(false); setEditing(null); }} className="px-4 py-2 rounded-lg border border-zinc-200 text-sm hover:bg-zinc-50">{tc("actions.cancel")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, testid, required, textarea }) {
  const Tag = textarea ? "textarea" : "input";
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{label}{required && <span className="text-red-600"> *</span>}</label>
      <Tag data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} required={required} rows={textarea ? 3 : undefined} className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
    </div>
  );
}
